/* Welcome popup — trigger, tile-flip game, dismissal memory.

   Scroll discipline (client bug, 4 Aug: "interacting with the popup forces a
   user's scroll downscreen"): `body { overflow: hidden }` is not a scroll
   lock on iOS Safari — touches on the takeover still chain-scrolled the page
   underneath it. The lock is now the position:fixed pattern: the body is
   pinned at its current scroll offset while the popup is open, and restored
   to the exact same offset on close. Every programmatic focus() in this file
   carries { preventScroll: true } for the same reason.

   The stamp is written on an EXPLICIT exit only — close, decline, a claim
   the visitor actually completed, or a validated signup at the moment it
   actually navigates away. It used to fire on the email submit CLICK, so a
   signup that never validated still burned the cooldown; stamping when the
   post really leaves keeps that fix without the reopen-after-signup nag.

   The signup posts asynchronously. {% form 'customer' %} is a normal form
   POST to /contact, which navigates the whole page away from whatever the
   visitor was reading and lands them on a page they never asked for — a
   session-destroying way to collect an email inside a takeover. With JS off
   the form still posts normally and Shopify's own posted_successfully?
   branch renders on the way back. */
(() => {
  const root = document.querySelector('[data-wpop]');
  if (!root) return;
  // ?nowpop=1 keeps screenshot/QA runs clean of the takeover.
  if (new URLSearchParams(location.search).has('nowpop')) return;

  const KEY = 'strum:wpop-dismissed';
  const panel = root.querySelector('[data-wpop-panel]');
  const stages = {
    game: root.querySelector('[data-wpop-stage="game"]'),
    win: root.querySelector('[data-wpop-stage="win"]'),
  };
  const designMode = root.hasAttribute('data-design-mode');
  const cooldownMs =
    Number(root.dataset.cooldownDays || 14) * 24 * 60 * 60 * 1000;
  const reducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const strings = {
    ok: root.dataset.signupOk || 'Saved.',
    fail: root.dataset.signupFail || 'That did not save. Please try again.',
  };

  const suppressed = () => {
    try {
      const stamp = Number(localStorage.getItem(KEY) || 0);
      return stamp && Date.now() - stamp < cooldownMs;
    } catch { return false; }
  };
  const stamp = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch {}
  };

  // iOS-safe scroll lock. overflow:hidden alone leaves the page scrollable
  // under the takeover on touch devices — every drag on the popup walked the
  // page down-screen. Pinning the body at its scroll offset makes the page
  // inert; the offset is restored on close so nothing visibly moves.
  let lockY = 0;
  const lockScroll = () => {
    lockY = window.scrollY || document.documentElement.scrollTop || 0;
    // Compensate for the vanishing scrollbar so the page doesn't shift.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    const s = document.body.style;
    s.position = 'fixed';
    s.top = `-${lockY}px`;
    s.left = '0';
    s.right = '0';
    s.width = '100%';
    s.overflow = 'hidden';
    if (gap > 0) s.paddingRight = `${gap}px`;
  };
  const unlockScroll = () => {
    const s = document.body.style;
    s.position = '';
    s.top = '';
    s.left = '';
    s.right = '';
    s.width = '';
    s.overflow = '';
    s.paddingRight = '';
    // Instant, not smooth: this restores where the visitor already was.
    window.scrollTo(0, lockY);
  };

  let opened = false;
  // Whatever held focus when the takeover stole it. Restored on close so a
  // keyboard user resumes where they were instead of at the top of the DOM.
  let opener = null;
  const open = () => {
    if (opened) return;
    opened = true;
    opener = document.activeElement;
    root.hidden = false;
    lockScroll();
    // hidden -> visible needs a style flush between them or the transition
    // skips. NOT requestAnimationFrame: rAF doesn't run while the window is
    // occluded, which left the popup "open" invisibly — scrim active, page
    // scroll-locked, nothing on screen to close — until the window surfaced.
    void root.offsetHeight;
    root.classList.add('is-open');
    panel.focus({ preventScroll: true });
  };
  const restoreFocus = () => {
    if (opener && opener.isConnected && opener !== document.body) {
      opener.focus({ preventScroll: true });
      return;
    }
    // No usable opener (the timer fired while nothing was focused). Park focus
    // on the page's own anchor rather than leaving it on a hidden dialog.
    const anchor = document.querySelector('main, #MainContent, body');
    if (!anchor) return;
    if (!anchor.hasAttribute('tabindex')) anchor.setAttribute('tabindex', '-1');
    anchor.focus({ preventScroll: true });
  };
  const close = () => {
    root.classList.remove('is-open');
    unlockScroll();
    stamp();
    restoreFocus();
    setTimeout(() => { root.hidden = true; }, 300);
  };

  // --- Triggers ------------------------------------------------------------
  if (designMode) {
    open();
  } else if (!suppressed()) {
    const timer = setTimeout(open, Number(root.dataset.delay || 5000));
    if (root.dataset.exitIntent === 'true') {
      document.addEventListener('mouseout', (event) => {
        if (!event.relatedTarget && event.clientY <= 0) {
          clearTimeout(timer);
          open();
        }
      });
    }
  }

  // --- Dismissal -----------------------------------------------------------
  root.querySelectorAll('[data-wpop-close]').forEach((el) =>
    el.addEventListener('click', close));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && opened && !root.hidden) close();
  });

  // The claim link navigates to the buy box — a completed exit, so it CLOSES
  // (close() stamps). Closing before the browser follows the href matters on
  // the PDP, where the link is a same-page #buy-box anchor: the popup used to
  // stay open over the freshly-scrolled page, and the new fixed-body lock
  // would otherwise swallow the jump entirely.
  // The signup submit is NOT in this set; it stamps on success instead.
  root.querySelectorAll('[data-wpop-claim]').forEach((el) =>
    el.addEventListener('click', close));

  // --- Signup --------------------------------------------------------------
  // Shopify rejects ANY non-navigation POST to /contact on this store with a
  // 400 (fetch/XHR, either encoding — verified 4 Aug 2026), so the old
  // AJAX-into-the-form approach can never succeed. Two working paths:
  //
  //  · Klaviyo list ID configured → the popup subscribes through Klaviyo's
  //    client API instead (no page reload, inline confirmation). Klaviyo is
  //    the system of record — exactly the client's "handle it in Klaviyo" —
  //    and its Shopify sync carries the subscriber back to Shopify.
  //  · No list ID yet → hand Klaviyo the identify + metric first (with a
  //    breath to flush), then release the NATIVE submit; Shopify captures
  //    the address and reloads with ?customer_posted=true, which open()'s
  //    suppression check treats as a completed signup (stamped below).
  const form = root.querySelector('form');
  const note = root.querySelector('[data-wpop-form-note]');

  // bfcache recovery. The native signup submit (and the claim link) navigate
  // away while the popup is still open and the body is pinned — and on this
  // store /contact posts detour through Shopify's bot challenge first. A
  // visitor who taps Back from that interstitial gets the page restored from
  // bfcache EXACTLY as it left: popup open, submit disabled, sending=true,
  // page unscrollable — "the popup broke and I'm stuck". pageshow(persisted)
  // is the only hook that runs on that restore: revive an open popup's form,
  // and finish any close the 300ms hide timer never got to.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    if (opened && root.classList.contains('is-open')) {
      if (form) {
        form.dataset.sending = 'false';
        const submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = false;
      }
      return;
    }
    root.hidden = true;
    if (document.body.style.position === 'fixed') unlockScroll();
  });

  // Native-submit landing: the reload lost the popup's in-page confirmation,
  // so at least burn the cooldown — a shopper who just signed up must not be
  // asked again by the next page.
  if (/[?&]customer_posted=true/.test(window.location.search)) stamp();

  const confirmInline = () => {
    const row = form.querySelector('.s-wpop__row');
    const label = form.querySelector('.s-wpop__label');
    if (row) row.hidden = true;
    if (label) label.hidden = true;
    if (note) { note.textContent = strings.ok; note.hidden = false; }
    stamp();
  };

  if (form) {
    form.addEventListener('submit', (event) => {
      const submit = form.querySelector('[type="submit"]');
      if (form.dataset.sending === 'true') { event.preventDefault(); return; }
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
      const email = (form.querySelector('input[type="email"]') || {}).value || '';
      const listId = root.dataset.klaviyoList || '';

      if (listId && klaviyoCompanyId() && window.fetch) {
        // Klaviyo-native path: subscribe with consent into the configured
        // list; no reload, popup confirms in place.
        event.preventDefault();
        form.dataset.sending = 'true';
        if (submit) submit.disabled = true;
        subscribeViaKlaviyo(email, listId)
          .then((ok) => {
            if (!ok) throw new Error('subscribe failed');
            identifyInKlaviyo(email);
            confirmInline();
          })
          .catch(() => {
            if (note) { note.textContent = strings.fail; note.hidden = false; }
            if (submit) submit.disabled = false;
            form.dataset.sending = 'false';
          });
        return;
      }

      // Shopify-native path: give the Klaviyo identify a moment to flush,
      // then let the real submission through (a second submit() skips this
      // handler via the sending flag).
      event.preventDefault();
      form.dataset.sending = 'true';
      if (submit) submit.disabled = true;
      identifyInKlaviyo(email);
      // Stamp at the moment of actual navigation, not on click: the form has
      // validated and the post is really leaving. The ?customer_posted=true
      // stamp below can't be relied on — the bot-challenge interstitial this
      // store puts in front of /contact drops the param, and a takeover that
      // re-opens 5s later for a visitor who JUST handed over their email is
      // the "popup got me stuck" report in its politest form.
      setTimeout(() => { stamp(); form.submit(); }, 350);
    });
  }

  // --- Klaviyo hand-off ----------------------------------------------------
  // The Shopify customer form above is the base capture (works with JS off;
  // the Klaviyo↔Shopify integration syncs those subscribers on its own
  // schedule). This layer makes the same lead land in the store's own Klaviyo
  // account IMMEDIATELY and with popup attribution: identify the profile,
  // fire a "Claimed Welcome Offer" metric a flow can trigger on, and — when
  // a list ID is set in the section settings — subscribe through Klaviyo's
  // public client API (proper consent; the list's double-opt-in setting is
  // honoured server-side). The company id is read off Klaviyo's own onsite
  // script tag, so nothing is hardcoded; if the Klaviyo app isn't installed
  // every branch is a silent no-op and the Shopify path still stands. Only
  // ever called AFTER the shopper submits their email to this store's signup
  // form — this is the delivery of that submission, not tracking.
  function klaviyoCompanyId() {
    const tag = document.querySelector('script[src*="klaviyo.com/onsite/js"][src*="company_id="]');
    if (!tag) return '';
    try { return new URL(tag.src).searchParams.get('company_id') || ''; } catch (e) { return ''; }
  }

  function identifyInKlaviyo(email) {
    if (!email) return;
    try {
      if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
        window.klaviyo.identify({ email: email });
        if (typeof window.klaviyo.track === 'function') {
          window.klaviyo.track('Claimed Welcome Offer', { source: 'welcome-popup' });
        }
      }
    } catch (e) { /* analytics must never break the popup */ }
  }

  function subscribeViaKlaviyo(email, listId) {
    const companyId = klaviyoCompanyId();
    if (!email || !listId || !companyId) return Promise.resolve(false);
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Welcome popup',
            profile: { data: { type: 'profile', attributes: { email: email } } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        },
      }),
    }).then((r) => r.ok).catch(() => false);
  }

  // Minimal focus trap — a takeover that tabs into the page under the scrim
  // strands keyboard users.
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusables = panel.querySelectorAll(
      'button, a[href], input, [tabindex]:not([tabindex="-1"])');
    const visible = [...focusables].filter((el) => el.offsetParent !== null);
    if (!visible.length) return;
    const first = visible[0];
    const last = visible[visible.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus({ preventScroll: true });
    }
  });

  // --- Game ----------------------------------------------------------------
  const confetti = () => {
    const host = root.querySelector('[data-wpop-confetti]');
    if (!host || reducedMotion) return;
    const colors = ['var(--rose)', 'var(--gold)', 'var(--blush-100)', 'var(--espresso)'];
    for (let i = 0; i < 60; i += 1) {
      const bit = document.createElement('span');
      bit.className = 's-wpop__bit';
      bit.style.left = `${Math.random() * 100}%`;
      bit.style.background = colors[i % colors.length];
      bit.style.animationDelay = `${Math.random() * 500}ms`;
      host.appendChild(bit);
    }
    setTimeout(() => host.replaceChildren(), 4000);
  };

  const cards = [...root.querySelectorAll('[data-wpop-card]')];
  let played = false;
  cards.forEach((card) =>
    card.addEventListener('click', () => {
      if (played) return;
      played = true;
      cards.forEach((c) => {
        c.classList.add('is-flipped', c === card ? 'is-win' : 'is-miss');
        c.disabled = true;
      });
      // Long enough to read the flip, short enough to not lose them.
      setTimeout(() => {
        stages.game.hidden = true;
        stages.win.hidden = false;
        confetti();
        panel.focus({ preventScroll: true });
      }, reducedMotion ? 150 : 1100);
    }));
})();
